import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { stripePromise } from '../stripe';
import { CREATE_CHECKOUT_SESSION_URL } from '../config';
import { PLAN_CONFIG } from '../constants/plans';
import { setClarityUserId } from '../utils/clarity';
import { CheckoutGeoipInfo } from '../components/CheckoutGeoipInfo';
import {
  checkoutV2Strings,
  getCheckoutV2Locale,
  getLocalizedPlanAmount,
} from '../constants/checkoutV2Locales';
import { getCheckoutV2Background } from '../constants/checkoutV2Backgrounds';
import { getHostname } from '../utils/getHostname';
import { CheckoutTrustBadges } from '../components/CheckoutTrustBadges';
import './Checkout.css';

function StripeCheckoutForm({ onSuccess, onError, disabled, formData, copy }) {
  const checkoutResult = useCheckout();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (checkoutResult.type !== 'success' || !checkoutResult.checkout) {
      return;
    }

    if (formData?.email && formData?.password) {
      sessionStorage.setItem(
        'stripe_checkout_pending',
        JSON.stringify(formData)
      );
    }

    const { confirm } = checkoutResult.checkout;
    try {
      const result = await confirm();
      if (result && result.error) {
        sessionStorage.removeItem('stripe_checkout_pending');
        onError?.(result.error.message || copy.paymentFailed);
      } else {
        sessionStorage.removeItem('stripe_checkout_pending');
        onSuccess?.();
      }
    } catch (err) {
      sessionStorage.removeItem('stripe_checkout_pending');
      onError?.(err.message || copy.errorOccurred);
    }
  };

  if (checkoutResult.type === 'loading') {
    return (
      <div className="checkout-loading">
        {copy.loadingPaymentForm}
      </div>
    );
  }
  if (checkoutResult.type === 'error') {
    return (
      <div className="checkout-form-error">
        {checkoutResult.error?.message || copy.failedLoadPaymentForm}
      </div>
    );
  }

  return (
    <>
      <PaymentElement />
      <button
        type="button"
        className="checkout-btn-primary"
        disabled={disabled}
        onClick={handleSubmit}
        style={{ marginTop: '16px', fontSize: '16px' }}
      >
        {copy.payNow}
      </button>
    </>
  );
}

function CheckoutV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locale = getCheckoutV2Locale(searchParams);
  const background = getCheckoutV2Background(searchParams);
  const str = checkoutV2Strings(locale);
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isActiveSubscriptionError, setIsActiveSubscriptionError] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [selectedPlanKey, setSelectedPlanKey] = useState('vpn_30_day');

  useDocumentTitle(str.documentTitle);

  const planConfig = PLAN_CONFIG;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (plan && planConfig[plan]) {
      setSelectedPlanKey(plan);
    }
  }, []);

  // Store affid and clickid from query params in localStorage; fallback to existing localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const affid = params.get('affid');
    const clickid = params.get('clickid');
    if (affid != null && affid !== '') {
      localStorage.setItem('vpnnext_affid', affid);
    }
    if (clickid != null && clickid !== '') {
      localStorage.setItem('vpnnext_clickid', clickid);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const pending = sessionStorage.getItem('stripe_checkout_pending');
    if (!pending) return;

    try {
      const { email, password } = JSON.parse(pending);
      sessionStorage.removeItem('stripe_checkout_pending');
      setFormData({ email, password });
      navigate(`/success?session_id=${sessionId}`, { replace: true });
    } catch {
      sessionStorage.removeItem('stripe_checkout_pending');
    }
  }, [navigate]);

  const generatePassword = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `vpn-${Math.random().toString(36).slice(2, 10)}`;
  };

  const amount = getLocalizedPlanAmount(selectedPlanKey, locale);

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();

    if (!email) {
      setPaymentError(str.pleaseEnterEmail);
      return;
    }

    setClarityUserId(email);
    setPaymentError(null);
    setIsActiveSubscriptionError(false);

    const password = generatePassword();
    setFormData({ email, password });
    setIsLoadingCheckout(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const affid =
        params.get('affid') ?? localStorage.getItem('vpnnext_affid') ?? '';
      const clickid =
        params.get('clickid') ?? localStorage.getItem('vpnnext_clickid') ?? '';

      const returnUrl = `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`;
      const checkoutBody = {
        customer_email: email,
        password,
        plan: selectedPlanKey,
        product_name: `${str.productNamePrefix}${amount.label}`,
        price_lookup_key: selectedPlanKey,
        return_url: returnUrl,
        origin: window.location.origin,
        affid,
        clickid,
      };
      if (locale !== 'en') {
        checkoutBody.locale = locale;
      }

      const response = await fetch(CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutBody),
      });

      const text = await response.text();
      if (!text) {
        setPaymentError(str.emptyServerResponse);
        setIsLoadingCheckout(false);
        return;
      }

      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        setPaymentError(str.invalidServerResponse);
        setIsLoadingCheckout(false);
        return;
      }

      const body = data.body ?? data;
      const statusCode = data.statusCode ?? response.status;

      if (statusCode === 409) {
        setIsActiveSubscriptionError(true);
        setPaymentError(
          body?.error || str.activeSubscriptionFallback
        );
        setIsLoadingCheckout(false);
        return;
      }

      if (body?.error) {
        setPaymentError(body.error);
        setIsLoadingCheckout(false);
        return;
      }

      const secret = body.clientSecret;
      if (!secret) {
        setPaymentError(str.invalidMissingSecret);
        setIsLoadingCheckout(false);
        return;
      }

      setClientSecret(secret);
      if (body.sessionId) setCheckoutSessionId(body.sessionId);
    } catch (err) {
      setPaymentError(
        err.message || str.initCheckoutFailed
      );
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentError(null);
    const sessionId = checkoutSessionId;
    setClientSecret(null);
    setCheckoutSessionId(null);
    if (sessionId) {
      navigate(`/success?session_id=${sessionId}`, { replace: true });
    } else {
      setPaymentError(str.missingSession);
    }
  };

  return (
    <div
      lang={locale}
      className={`checkout-page checkout-page--v2${
        background ? ' checkout-page--custom-bg' : ''
      }${
        background?.theme === 'concert' ? ' checkout-page--bg-concert' : ''
      }${
        background?.theme === 'sports' ? ' checkout-page--bg-sports' : ''
      }`}
      style={
        background
          ? { '--checkout-bg-image': `url(${background.src})` }
          : undefined
      }
    >
      <div className="checkout-card">
        <div className="checkout-geoip-slot">
          <CheckoutGeoipInfo locale={locale} />
        </div>
        <h1 className="checkout-header-title">
          {str.completeRegistration}
        </h1>
        <div className="checkout-columns">
          <div className="checkout-left">
            <div className="checkout-v2-left-content">
              <div className="checkout-v2-logo-container checkout-logo-row">
                <img
                  src="/images/logo.svg"
                  alt={str.vpnLogoAlt}
                  className="checkout-logo"
                />
                <div>
                  <div className="checkout-brand-name">
                    {getHostname()}
                  </div>
                  <div className="checkout-plan-desc">
                    {amount.description}
                  </div>
                </div>
              </div>
              <div className="checkout-v2-benefits-container checkout-benefits-wrap">
                <div className="checkout-benefits-inner">
                  <h2 className="checkout-benefits-title">
                    {str.useVpnUnlock}
                  </h2>
                  <ul className="checkout-benefits-list">
                    <li>{str.streamingServices}</li>
                    <li>{str.onlineContent}</li>
                    <li>{str.socialMedia}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="checkout-right">
            <form className="checkout-form" onSubmit={handleProceedToPayment}>
              <label className="checkout-label">
                {str.emailLabel}{' '}
                <span className="checkout-required">*</span>
              </label>
              <input
                type="email"
                placeholder={str.emailPlaceholder}
                required
                className="checkout-input"
              />

              <h3 className="checkout-section-title" style={{ marginTop: '20px' }}>
                {str.orderSummary}
              </h3>
              <div className="checkout-order-summary" style={{ marginBottom: '32px' }}>
                <table className="checkout-order-table">
                  <tbody>
                    <tr>
                      <td>{str.plan}</td>
                      <td>{amount.label}</td>
                    </tr>
                    <tr>
                      <td>{str.billing}</td>
                      <td>{str.billingOnce}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="checkout-order-total">
                  <span>{str.totalDueToday}</span>
                  <span>${amount.display}</span>
                </div>
              </div>

              {clientSecret ? (
                <CheckoutProvider
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    elementsOptions: {
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          colorPrimary: '#2563eb',
                          borderRadius: '8px',
                        },
                      },
                    },
                  }}
                >
                  <div className="checkout-account-box">
                    <span className="checkout-account-label">
                      {str.accountLabel}
                    </span>{' '}
                    {formData.email}
                  </div>
                  {paymentError && (
                    <p className="checkout-error">
                      {paymentError}
                    </p>
                  )}
                  <StripeCheckoutForm
                    onSuccess={handlePaymentSuccess}
                    onError={setPaymentError}
                    disabled={false}
                    formData={formData}
                    copy={str}
                  />
                </CheckoutProvider>
              ) : (
                <>
                  <button
                    type="submit"
                    className="checkout-btn-primary"
                    disabled={isLoadingCheckout}
                  >
                    {isLoadingCheckout
                      ? str.loading
                      : str.continueSecurePayment}
                  </button>
                  {paymentError && (
                    <div
                      className={`checkout-error${isActiveSubscriptionError ? ' checkout-error--warning' : ''}`}
                      style={{ marginTop: '12px' }}
                    >
                      {paymentError}
                      {isActiveSubscriptionError && (
                        <div style={{ marginTop: '10px' }}>
                          <Link to="/login" className="checkout-link">
                            {str.logInInstead}
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className={`checkout-legal${locale !== 'en' ? ' checkout-legal--wrap' : ''}`}>
                {str.agreePrefix}<a href="/tos" className="checkout-link">{str.termsOfService}</a> {str.andWord} <a href="/privacy-policy" className="checkout-link">{str.privacyPolicy}</a>{str.agreeSuffix}
              </div>
              <CheckoutTrustBadges
                labels={{
                  secure: str.secure,
                  encrypted: str.encrypted,
                  pciDss: str.pciDss,
                }}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutV2;
